import { IStockCard } from 'interfaces/IStockCard'
import {
  STOCK_CARD_TYPE_PACKAGING,
  STOCK_CARD_TYPE_PREMIX,
  STOCK_CARD_TYPE_HALF_COMPLETE,
  STOCK_CARD_TYPE_INGREDIENTS,
  STOCK_CARD_TYPE_LABEL,
} from 'enums/stockCard'
import { IProduction } from 'interfaces/IProduction'

export const cleanCorruptedString = (value: string | null | undefined): string => {
  if (!value) return ''

  // Heuristic: if it has trailing whitespace AND ends with '0' after trim
  // We assume it's the corruption pattern.
  // We also check if it's NOT just whitespace.
  if (value.trim().length === 0) return ''

  // Check for trailing whitespace in original string
  // invisible trailing whitespace could be ' ' or '\u00A0' etc.
  const hasTrailingWhitespace = /\s$/.test(value)

  const trimmed = value.trim()

  if (hasTrailingWhitespace && trimmed.endsWith('0')) {
    return trimmed.slice(0, -1)
  }

  return trimmed
}

export const getStockCardDescription = (stockCard: IStockCard, production?: IProduction): string => {
  if (stockCard.type === STOCK_CARD_TYPE_PACKAGING) {
     let packagingId = stockCard.packagingId

     // Fallback if missing
     if (!packagingId) {
         packagingId = stockCard.stockCardPackaging?.[0]?.packaging?.packagingId ||
                       stockCard.stockCardPackaging?.[0]?.packagingId

         if (!packagingId && production && production.packagingId) {
             packagingId = production.packagingId
         }
     }

     let packagingName = stockCard.packagingName

     // Fallback/Correction if corrupted or missing
     const nestedName = stockCard.stockCardPackaging?.[0]?.packaging?.packagingName ||
                        stockCard.stockCardPackaging?.[0]?.partName

     if (packagingName) {
         const cleanedName = cleanCorruptedString(packagingName)
         // Verification logic (still useful if heuristic fails or if no trailing space)
         const trimmedPackagingName = packagingName.trim()

         if (trimmedPackagingName.endsWith('0')) {
             if (nestedName && !nestedName.trim().endsWith('0')) {
                 packagingName = nestedName
             } else if (production && production.packagingName) {
                 const trimmedProductionName = production.packagingName.trim()
                 if (trimmedProductionName === trimmedPackagingName.slice(0, -1)) {
                     packagingName = trimmedProductionName
                 }
             } else if (cleanedName !== trimmedPackagingName) {
                 // Heuristic applied
                 packagingName = cleanedName
             }
         }
     } else if (!packagingName && nestedName) {
         packagingName = nestedName
     }

     return `${packagingId || ''} - ${packagingName}`
  }

  let typeLabel = ''
  switch (stockCard.type) {
    case STOCK_CARD_TYPE_PREMIX:
    case STOCK_CARD_TYPE_HALF_COMPLETE:
      typeLabel = 'Ruahan'
      break
    case STOCK_CARD_TYPE_INGREDIENTS:
      typeLabel = 'Bahan Baku'
      break
    case STOCK_CARD_TYPE_LABEL:
      typeLabel = 'Label'
      break
    default:
      typeLabel = stockCard.type
      break
  }

  let batchNumber = stockCard.batchNumber

  // Fallback/Correction
  const nestedBatch = stockCard.stockCardBatches?.[0]?.batch?.batchNumber ||
                      stockCard.stockCardBatches?.[0]?.batchNumber

  if (!batchNumber || batchNumber === '-') {
      batchNumber = nestedBatch || '-'
  }

  // Handle extra 0 in batchNumber
  if (batchNumber && batchNumber !== '-') {
      const cleanedBatch = cleanCorruptedString(batchNumber)
      const trimmedBatchNumber = batchNumber.trim()

      if (trimmedBatchNumber.endsWith('0')) {
          if (nestedBatch && !nestedBatch.trim().endsWith('0')) {
              batchNumber = nestedBatch
          } else if (production && production.batches) {
              const matchingBatch = production.batches.find(b => b.batchNumber.trim() === trimmedBatchNumber.slice(0, -1))
              if (matchingBatch) {
                  batchNumber = matchingBatch.batchNumber.trim()
              }
          } else if (cleanedBatch !== trimmedBatchNumber) {
              // Heuristic applied
              batchNumber = cleanedBatch
          }
      }
  }

  return `${typeLabel} - Bets: ${batchNumber}`
}
